import { Component, input, output, model, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormsModule, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { UserService } from '../../services/user.service';
import { CatalogService } from '@services/data/catalog.service';
import Swal from 'sweetalert2';
import { FormMode } from 'app/core/constants/form-mode';
import { PermissionService } from '@services/core/permission.service';

@Component({
    selector: 'app-user-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormsModule, NgSelectModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './user-form.component.html'
})
export class UserFormComponent {

    userForm = input.required<FormGroup>();
    formChange = output<FormGroup>();
    mode = model<FormMode>(FormMode.CREATE);
    formMode = FormMode;
    parties = input<any[]>([]);

    maxDate = input<string>();
    minDate = input<string>();

    private userService = inject(UserService);
    public catalogService = inject(CatalogService);
    readonly perm = inject(PermissionService);

    submit() {
        if (this.userForm() && this.userForm().valid) {
            this.formChange.emit(this.userForm());
        }
    }

    editMode() {
        this.mode.set(this.formMode.EDIT);
        this._syncPartyRequirement();
    }

    searchParty = (term: string, item: any) => {
        const q = (term || '').toLowerCase();
        if (!q) return true;
        return `${item?.rut || ''} ${item?.names || ''} ${item?.uuid || ''}`.toLowerCase().includes(q);
    };

    findRole(identifier: any): string {
        if (identifier == null) return '';
        const str = String(identifier).trim().toUpperCase();
        const num = Number(identifier);
        const role = (this.catalogService.roles() || []).find((item: any) =>
            String(item.slug || item.name || '').trim().toUpperCase() === str ||
            (Number.isFinite(num) && Number(item.id) === num)
        );
        return role ? (role.displayName || role.description || role.slug || role.name) : '';
    }

    roleLabels(roles: any[]): string {
        if (!Array.isArray(roles) || !roles.length) return '—';
        return roles
            .map((r) => this.findRole(typeof r === 'object' ? (r.slug ?? r.name ?? r.id) : r))
            .filter(Boolean)
            .join(', ') || '—';
    }

    onRolesChange(values: any[]) {
        const form = this.userForm();
        const list = Array.isArray(values) ? values.filter(Boolean) : [];
        form.get('roleSlugs')?.setValue(list);
        const current = form.get('roleSlug')?.value;
        if (!list.length) {
            form.get('roleSlug')?.setValue(null);
            this._syncPartyRequirement();
            return;
        }
        if (!list.includes(current)) {
            form.get('roleSlug')?.setValue(list[0]);
        }
        this._syncPartyRequirement();
    }

    isUserRole(role: any): boolean {
        const slug = String(role?.slug || role?.name || role || '').trim().toUpperCase();
        return slug === 'USER';
    }

    hasUserRole(): boolean {
        const values = this.userForm().get('roleSlugs')?.value || this.userForm().get('roleIds')?.value || [];
        return (values || []).some((v: any) => this.isUserRole(v));
    }

    trackByParty = (item: any) => item?.uuid ?? item;

    partyLabel(party: any): string {
        if (!party) return '';
        if (typeof party === 'string') {
            const found = (this.parties() || []).find((p: any) => String(p?.uuid) === String(party));
            return found ? this.partyLabel(found) : party;
        }
        const rut = party.rut ?? '';
        const names = party.names ?? '';
        return [rut, names].filter(Boolean).join(' — ') || String(party.uuid ?? '');
    }

    partyDisplay(): string {
        const uuid = this.userForm().get('partyUuid')?.value;
        if (!uuid) return '—';
        const party = (this.parties() || []).find((p: any) => String(p.uuid) === String(uuid))
            || this.userForm().get('party')?.value;
        return this.partyLabel(party) || String(uuid);
    }

    private _syncPartyRequirement() {
        const form = this.userForm();
        const ctrl = form.get('partyUuid');
        if (!ctrl) return;
        if (this.hasUserRole()) {
            ctrl.setValidators([Validators.required]);
        } else {
            ctrl.clearValidators();
            ctrl.setValue(null);
        }
        ctrl.updateValueAndValidity({ emitEvent: false });
    }

    updateUser(isActive: number) {
        const uuid = this.userForm().get('uuid')?.value;
        const action = isActive === 1 ? 'habilitar' : 'desactivar';
        Swal.fire({
            title: '¿Estás seguro de ' + action + ' este usuario?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#348FE2',
            cancelButtonColor: '#FF5B57',
            cancelButtonText: 'Cancelar',
            confirmButtonText: 'Sí, ' + action
        }).then((result) => {
            if (result.isConfirmed) {
                this.userService.update({ id: uuid, isActive: isActive }).subscribe({
                    next: () => {
                        Swal.fire(
                            action,
                            'El usuario ha sido ' + action,
                            'success'
                        );
                        this.formChange.emit(null);
                    },
                    error: (err) => Swal.fire('Error', 'No se pudo ' + action + ' el usuario', 'error')
                });
            }
        })
    }
}
